// ---------------------------------------------------------------------------
// ProductSingle context
// ---------------------------------------------------------------------------
//
// Wraps all PDP state and side-effects into a single provider so
// `<ProductSingle>` and its descendants don't have to own any of the
// orchestration. Mirrors the pattern used by `~/context/basket`: a
// typed context value, a `useProductSingleContext` hook that throws
// when used outside the provider, and a `ProductSingleProvider`
// component that owns the underlying hooks and effects.
//
// The provider internally calls:
//
//   - `useProductSingle(...)` — PDP state (variants, address,
//     fulfilment, nearestPickup, basket flow handlers, etc.)
//   - `useBrendaCharges()` — the charges fetch + resolution flow.
//
// It also owns:
//
//   - The sticky-button visibility ref + `useOnScreen` hook.
//   - Variant-tracking side-effect (snowplow fires on variant change).
//   - Route-change reset (clears pricing when navigating products).
//   - Basket flow trigger (opens check-address modal or fires the
//     configured basket flow once pricing data is committed).
//
// Consumers read state via `useProductSingleContext()` and let the
// provider handle everything else.
// ---------------------------------------------------------------------------

// Global imports
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation, useRouteLoaderData } from '@remix-run/react'

// App imports
import { useBrendaCharges, useOnScreen, useProductSingle } from '~/hooks'
import { checkNumberOfDays, snowplowTrackProductView } from '~/utils'
import { chooseBrendaHireEndDate } from '~/utils/brendaDates'
import { checkCartAddress } from '~/utils/basket'

// Types
import type { MutableRefObject, ReactNode } from 'react'
import type {
  CustomerAddressProps,
  LunarProductPreBasket,
  ProductDetailsProps,
  ProductDetailsVariantProps,
  ProductTrackingObject,
  RootDataProps,
} from '~/types'
import type {
  BrendaChargesErrorState,
  BrendaChargesPickupPoint,
  BrendaChargesResult,
  BrendaChargesSubmit,
} from '~/types/brendaCharges'

// ---------------------------------------------------------------------------
// Context shape
// ---------------------------------------------------------------------------

/**
 * Everything a PDP child needs, in one object. Grouped by concern:
 *
 *   - Product / variant selection
 *   - Address + fulfilment selection
 *   - Charges fetch state (from `useBrendaCharges`)
 *   - Legacy-shape bridges (deleted once downstream migrates)
 *   - Basket flow actions
 *   - UI modal state
 *   - Layout refs (carousel + sticky-button visibility)
 */
export interface ProductSingleContextValue {
  // ------------------------------------------------------------------
  // Product + variants
  // ------------------------------------------------------------------
  product: ProductDetailsProps
  /** Primary category string derived from product attributes. */
  category: string
  /** The variant slug currently selected via the URL query. */
  selectedVariant: string
  activeVariant: ProductDetailsVariantProps | null
  fromPrice: ReturnType<typeof useProductSingle>['fromPrice']
  salePrice: ReturnType<typeof useProductSingle>['salePrice']
  averageHire: ReturnType<typeof useProductSingle>['averageHire']
  setQueryVariant: (value: string) => void

  // ------------------------------------------------------------------
  // Address + fulfilment
  // ------------------------------------------------------------------
  address: string
  addressData: CustomerAddressProps | null
  addressCheck: boolean
  fulfilment: string | null
  setAddress: (value: string) => void
  setAddressData: (value: CustomerAddressProps | null) => void
  setAddressCheck: (value: boolean) => void
  setFulfilment: (value: string | null) => void

  // ------------------------------------------------------------------
  // Pickup branch selection (PDP "Change branch" modal)
  // ------------------------------------------------------------------
  /** True when the pickup-branch modal is open. */
  pickupModalOpen: boolean
  setPickupModalOpen: (open: boolean) => void
  /**
   * The branch id the user explicitly chose in the modal, or null when
   * they're on the auto-selected nearest. Persists across charges
   * re-fetches (quantity / fulfilment) so the branch is re-pinned, and
   * is read when building re-fetch requests as `currentBranchId`.
   */
  userSelectedBranchId: number | null
  /** Pick a branch from the modal — client-side only, no re-fetch. */
  selectBranch: (point: BrendaChargesPickupPoint) => void

  // ------------------------------------------------------------------
  // Charges fetch state (from useBrendaCharges)
  // ------------------------------------------------------------------
  /** Most recently resolved charges result, or null before any fetch. */
  charges: BrendaChargesResult | null
  /** True while a charges request is in flight (including any re-fetch). */
  chargesLoading: boolean
  /** Latest charges error, or null. */
  chargesError: BrendaChargesErrorState | null
  /** Fire a charges request. Handles BH + re-fetch internally. */
  submitCharges: (
    submit: BrendaChargesSubmit,
    options?: {
      preserveSide?: 'delivery' | 'pickup'
      keepResult?: boolean
      preservePickupPoints?: boolean
    }
  ) => void
  /** Clear the current charges result + error. */
  clearCharges: () => void
  /**
   * Clears both the charges result and the committed product data —
   * used when the user changes dates / variant / address and we need
   * to invalidate everything pricing-related in one call.
   */
  resetPricing: () => void
  /** Bank holidays cached for the active postcode. */
  bankHolidays: ReturnType<typeof useBrendaCharges>['bankHolidays']

  // ------------------------------------------------------------------
  // Basket flow
  // ------------------------------------------------------------------
  basketProduct: LunarProductPreBasket | null
  basketLoading: boolean
  basketError: ReturnType<typeof useProductSingle>['basketError']
  setBasketProduct: (value: LunarProductPreBasket) => void
  setBasketError: ReturnType<typeof useProductSingle>['setBasketError']
  handleAddToBasket: ReturnType<typeof useProductSingle>['handleAddToBasket']
  chooseBasketFlow: ReturnType<typeof useProductSingle>['chooseBasketFlow']
  resetBasketFlow: ReturnType<typeof useProductSingle>['resetBasketFlow']

  // ------------------------------------------------------------------
  // Modal / UI state
  // ------------------------------------------------------------------
  addressModalOpen: boolean
  purchaseModalOpen: boolean
  setAddressModalOpen: (open: boolean) => void
  setPurchaseModalOpen: (open: boolean) => void

  // ------------------------------------------------------------------
  // Layout refs
  // ------------------------------------------------------------------
  /** Splide instance ref for the thumbnail gallery. */
  thumbsRef: ReturnType<typeof useProductSingle>['thumbsRef']
  /** Splide instance ref for the main gallery. */
  mainRef: ReturnType<typeof useProductSingle>['mainRef']
  /** Ref attached to the purchase block — used by the sticky button observer. */
  purchaseRef: MutableRefObject<HTMLDivElement | null>
  /** True when the purchase block is scrolled into view. */
  isPurchaseVisible: boolean
}

// ---------------------------------------------------------------------------
// Context + consumer hook
// ---------------------------------------------------------------------------

const ProductSingleContext = createContext<ProductSingleContextValue | null>(
  null
)

/**
 * Reads the current `ProductSingle` context. Throws when used outside
 * the provider — catches accidental misuse at dev time.
 */
export const useProductSingleContext = (): ProductSingleContextValue => {
  const ctx = useContext(ProductSingleContext)
  if (!ctx) {
    throw new Error(
      'useProductSingleContext must be used within a ProductSingleProvider'
    )
  }
  return ctx
}

// ---------------------------------------------------------------------------
// Small internal helpers
// ---------------------------------------------------------------------------

const hasVariantTrackingChanged = (
  prev: ProductTrackingObject | null,
  current: ProductTrackingObject | null
): boolean =>
  prev?.guidePrice?.amount !== current?.guidePrice?.amount ||
  prev?.stockCode !== current?.stockCode ||
  prev?.name !== current?.name ||
  prev?.salePrice?.amount !== current?.salePrice?.amount

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface ProductSingleProviderProps {
  /** The raw product data loaded by the PDP route. */
  product: ProductDetailsProps
  children: ReactNode
}

export const ProductSingleProvider = ({
  product,
  children,
}: ProductSingleProviderProps) => {
  const { cart } = useRouteLoaderData('root') as RootDataProps
  const location = useLocation()

  // Derive the product's primary category from its string attributes.
  const category = useMemo(
    () =>
      product.stringAttributes?.find(attr => attr.name === 'categories')
        ?.value ?? '',
    [product.stringAttributes]
  )

  // ------------------------------------------------------------------
  // Underlying hooks
  // ------------------------------------------------------------------

  // PDP state — variant selection, address/fulfilment, basket flow.
  const productSingle = useProductSingle({
    variants: product.variants,
    category,
  })

  // Charges fetch state — single source of truth for "what does Brenda
  // say about this product's pricing / availability right now".
  const {
    result: charges,
    loading: chargesLoading,
    error: chargesError,
    submitBrendaCharges: submitCharges,
    clear: clearCharges,
    bankHolidays,
  } = useBrendaCharges()

  // ------------------------------------------------------------------
  // Pickup-branch selection (PDP "Change branch" modal)
  // ------------------------------------------------------------------
  //
  // `userSelectedBranchId` is the persistent record of the explicit
  // choice; it survives re-fetches so quantity / fulfilment changes can
  // re-pin the branch via `currentBranchId`.

  const [pickupModalOpen, setPickupModalOpen] = useState(false)
  const [userSelectedBranchId, setUserSelectedBranchId] = useState<
    number | null
  >(null)

  // `resetPricing` — clears the charges result and any pickup-branch
  // selection. Called when the user changes address / dates / variant,
  // so the next fetch resolves the nearest branch afresh.
  const resetPricing = useCallback(() => {
    clearCharges()
    setUserSelectedBranchId(null)
  }, [clearCharges])

  // Pick a branch from the modal — re-fetches for the chosen branch so
  // pricing reflects its window, re-pinning it via `currentBranchId`.
  //   - `preserveSide: 'delivery'` keeps the delivery side's own date
  //     (the re-fetch is anchored on the pickup branch's window, which
  //     would otherwise drag delivery onto it).
  //   - `preservePickupPoints` keeps the modal's branch list anchored on
  //     the requested window (otherwise every branch collapses onto the
  //     chosen branch's date).
  // `preserveSide` also keeps the section on screen, so no full reload.
  const selectBranch = useCallback(
    (point: BrendaChargesPickupPoint) => {
      if (!charges) return

      const addressData = productSingle.addressData
      if (!addressData) return

      const { originalStartDate, originalEndDate, submittedStartDate } =
        charges.request
      const days = checkNumberOfDays(originalStartDate, originalEndDate)

      // Future-stock branch → its earliest date; current stock keeps the
      // requested window. End preserves the requested duration.
      const startDate = point.firstAvailableDate ?? submittedStartDate
      const endDate = chooseBrendaHireEndDate(days, startDate, bankHolidays)

      setUserSelectedBranchId(point.id)
      productSingle.setFulfilment('pickup')
      setPickupModalOpen(false)

      submitCharges(
        {
          stockCode: charges.product.stockCode,
          startDate,
          endDate,
          quantity: charges.request.quantity,
          postcode: addressData.postcode,
          address: { line: productSingle.address, data: addressData },
          skipAddressSave: true,
          currentBranchId: point.id,
          selectedFulfilment: 'pickup',
        },
        { preserveSide: 'delivery', preservePickupPoints: true }
      )
    },
    // `productSingle.*` are stable; deps intentionally narrow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [charges, bankHolidays]
  )

  // ------------------------------------------------------------------
  // Layout refs — sticky-button visibility
  // ------------------------------------------------------------------

  const purchaseRef = useRef<HTMLDivElement | null>(null)
  const isPurchaseVisible = useOnScreen(purchaseRef)

  // ------------------------------------------------------------------
  // Side-effect 1 — basket flow trigger
  // ------------------------------------------------------------------
  //
  // Once the user has committed to a basket line (basketProduct
  // populated), we either open the check-address modal (if the cart
  // already has a different delivery address) or fire the configured
  // basket flow directly. Needs the live `charges` result to decide
  // whether the product has extras worth showing.

  useEffect(() => {
    if (!productSingle.basketProduct || !charges) return

    if (
      !productSingle.addressCheck &&
      !productSingle.addressModalOpen &&
      productSingle.addressData &&
      checkCartAddress(productSingle.addressData, cart)
    ) {
      productSingle.setAddressModalOpen(true)
    } else if (!productSingle.addressModalOpen) {
      productSingle.chooseBasketFlow(productSingle.basketProduct, charges)
    }
    // @NOTE: ONLY RUNS ONCE WHEN basketProduct IS SET — deps are intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSingle.basketProduct])

  // ------------------------------------------------------------------
  // Side-effect 2 — variant tracking (snowplow)
  // ------------------------------------------------------------------
  //
  // @NOTE: Watching `activeVariant` directly caused additional tracking
  // events to fire when getPrices fetches re-ran. Using a tracking
  // snapshot ref to compare only the values we care about avoids that.

  const prevActiveTrackingRef = useRef<ProductTrackingObject | null>(null)

  useEffect(() => {
    const active = productSingle.activeVariant
    if (!active) return

    const trackingObject: ProductTrackingObject = {
      stockCode: active.stockCode,
      name: active.name,
      guidePrice: active.guidePrice,
      salePrice: active?.salePrice,
    }

    if (
      !hasVariantTrackingChanged(prevActiveTrackingRef.current, trackingObject)
    ) {
      return
    }

    if (
      trackingObject.stockCode &&
      trackingObject.name &&
      trackingObject.guidePrice
    ) {
      snowplowTrackProductView({
        ...trackingObject,
        category,
      })

      prevActiveTrackingRef.current = trackingObject

      // Clear hire data on variant change in case prices differ.
      resetPricing()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productSingle.activeVariant, category])

  // ------------------------------------------------------------------
  // Side-effect 3 — route change
  // ------------------------------------------------------------------
  //
  // Clear pricing data when the user navigates to a different product
  // so stale quotes don't leak across PDPs.

  useEffect(() => {
    resetPricing()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname])

  // ------------------------------------------------------------------
  // Context value
  // ------------------------------------------------------------------

  const value: ProductSingleContextValue = {
    // Product / variants
    product,
    category,
    selectedVariant: productSingle.selectedVariant,
    activeVariant: productSingle.activeVariant,
    fromPrice: productSingle.fromPrice,
    salePrice: productSingle.salePrice,
    averageHire: productSingle.averageHire,
    setQueryVariant: productSingle.setQueryVariant,

    // Address + fulfilment
    address: productSingle.address,
    addressData: productSingle.addressData,
    addressCheck: productSingle.addressCheck,
    fulfilment: productSingle.fulfilment,
    setAddress: productSingle.setAddress,
    setAddressData: productSingle.setAddressData,
    setAddressCheck: productSingle.setAddressCheck,
    setFulfilment: productSingle.setFulfilment,

    // Pickup branch selection
    pickupModalOpen,
    setPickupModalOpen,
    userSelectedBranchId,
    selectBranch,

    // Charges fetch state
    charges,
    chargesLoading,
    chargesError,
    submitCharges,
    clearCharges,
    resetPricing,
    bankHolidays,

    // Basket flow
    basketProduct: productSingle.basketProduct,
    basketLoading: productSingle.basketLoading,
    basketError: productSingle.basketError,
    setBasketProduct: productSingle.setBasketProduct,
    setBasketError: productSingle.setBasketError,
    handleAddToBasket: productSingle.handleAddToBasket,
    chooseBasketFlow: productSingle.chooseBasketFlow,
    resetBasketFlow: productSingle.resetBasketFlow,

    // Modal / UI state
    addressModalOpen: productSingle.addressModalOpen,
    purchaseModalOpen: productSingle.purchaseModalOpen,
    setAddressModalOpen: productSingle.setAddressModalOpen,
    setPurchaseModalOpen: productSingle.setPurchaseModalOpen,

    // Layout refs
    thumbsRef: productSingle.thumbsRef,
    mainRef: productSingle.mainRef,
    purchaseRef,
    isPurchaseVisible,
  }

  return (
    <ProductSingleContext.Provider value={value}>
      {children}
    </ProductSingleContext.Provider>
  )
}
